<!-- 
 * TableWithColumnDrawer
 * 可重用的表格元件，包含：
 * - 欄位顯示 Drawer（可勾選隱藏/顯示）
 * - 資料列選取 Checkbox
 * - 表格展示
 * - 分頁與切換每頁筆數
 *
 * 使用方式：
 * <TableWithColumnDrawer
 *   :column-options="columnOptions"
 *   :data="tableData"
 *   :total="totalCount"
 *   :loading="isLoading"
 *   :page-size="pageSize"
 *   :page-current="pageCurrent"
 *   :page-size-opts="[10, 20, 40, 100]"
 *   :total-text="$t('rcmtable.totalDataCountText')"
 *   :page-sizer-text="$t('rcmtable.page')"
 *   @change-page="handlePageChange"
 *   @change-page-size="handlePageSizeChange"
 *   @row-select-change="handleRowSelectChange"
 * />
 *
 * Props：
 * - columnOptions: 欄位設定陣列（必填）
 *     每個欄位例：
 *     {
 *       key: "rid",             // 欄位對應資料的 key
 *       title: "事件ID",        // 顯示的欄位標題
 *       checked: true,         // 是否預設顯示
 *       sortable: "true",      // 是否要排序功能（字串 "true"）
 *       isTime: true,          // 是否為時間欄位 → 自動格式化
 *     }
 * - data: 表格資料（陣列）
 * - total: 總筆數（Number）
 * - loading: 是否顯示 loading（Boolean）
 * - pageSize / pageCurrent: 分頁控制（Number）
 * - pageSizeOpts: 每頁筆數選項（Array）
 * - totalText / pageSizerText: 可自訂文字（String）
 *
 * Emit Events：
 * - change-page: 頁碼切換 → (page: Number)
 * - change-page-size: 每頁筆數切換 → (size: Number)
 * - row-select-change: 列選取改變 → (row: Object)
 *
 * 小提醒：
 * - 共用元件不會依據固定 key 處理欄位（如 @timestamp），只會依照 isTime 控制格式化
 * - 沒有時間欄位的頁面：不要設定 isTime，即可正常使用
 *
 -->

<template lang="pug">
div
    Card
        Row
            //- Drawer：欄位顯示（不包含 Checkbox 欄位）
            Drawer(
                :title="$t('rcmtable.showCols')"
                :closable="false"
                v-model="isShowDrawer"
                width="400"
            )
                div(v-for="col in internalColumnOptions" :key="col.key")
                    Checkbox(
                        v-model="col.checked"
                        :disabled="col.checked && checkedColumnCount === 1"
                    )
                        | {{ col.title }}

        Row(style="padding-bottom: 4px;")
            Col(:lg="24" :md="24" :sm="24")
                div(style="text-align: right; margin-bottom: 8px;")
                    Button(type="primary" @click="isShowDrawer = true") {{$t("rcmtable.showCols")}}

                Table(
                    :class="tableHeaderStyle"
                    :columns="tableColumns"
                    :data="data"
                    :loading="loading"
                    :no-data-text="$t('main.tableNoDataText')"
                )
                    // 固定第一欄：選取欄位
                    template(#select="{ row }")
                        Checkbox(
                            v-model="row.select"
                            @on-change="$emit('row-select-change', row)"
                        )

                Row(style="margin-top: 12px;")
                    span(:style="totalCountStyle") {{ totalText }} {{ total }}
                    Page(
                        show-sizer
                        :styles="paginationStyle"
                        :total="total < 10000 ? total : 10000"
                        :page-sizer-text="pageSizerText"
                        :page-size="pageSize"
                        :page-size-opts="pageSizeOpts"
                        :current="pageCurrent"
                        @on-change="onChangePage"
                        @on-page-size-change="onChangePageSize"
                    )
</template>

<script>
import moment from "moment";

export default {
    name: "TableWithColumnDrawer",
    props: {
        // 父層傳進來的欄位定義（不含Checkbox） Ex. BehaviorAnalyze 的 columnOptions
        columnOptions: {
            type: Array,
            required: true,
        },

        // 表格資料 & 狀態
        data: {
            type: Array,
            default: () => [],
        },
        total: {
            type: Number,
            default: 0,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        noDataText: {
            type: String,
            default: "目前沒有資料",
        },

        // 分頁
        pageSize: {
            type: Number,
            default: 10,
        },
        pageCurrent: {
            type: Number,
            default: 1,
        },
        pageSizeOpts: {
            type: Array,
            default: () => [10, 20, 40, 100],
        },

        // 顯示文字
        totalText: {
            type: String,
            default: "總筆數：",
        },
        pageSizerText: {
            type: String,
            default: "每頁筆數",
        },
    },
    data() {
        return {
            isShowDrawer: false,

            // 不要直接改 props，所以做一份內部使用的 copy
            internalColumnOptions: [],

            // 分頁樣式
            totalCountStyle: {
                float: "left",
                "line-height": "32px",
            },
            paginationStyle: {
                float: "right",
                margin: "0",
            },
        };
    },
    created() {
        this.initInternalColumnOptions();
    },
    watch: {
        // 如果父層之後有動態改 columnOptions，也能同步
        columnOptions: {
            deep: true,
            handler() {
                this.initInternalColumnOptions();
            },
        },
    },
    computed: {
        tableHeaderStyle() {
            return this.$store.getters.getTableConfig.headerStyle;
        },
        // Drawer 中目前勾選欄位的數量（至少要 1 個）
        checkedColumnCount() {
            return this.internalColumnOptions.filter((col) => col.checked).length;
        },
        // 根據勾選欄位顯示的「資料欄」 columns（不含 Checkbox 欄）
        displayedColumns() {
            return this.internalColumnOptions
                .filter((col) => col.checked)
                .map((col) => {
                    const base = {
                        title: col.title,
                        key: col.key,
                        minWidth: col.width,
                        sortable: col.sortable === "true",
                    };

                    // 如果欄位被標記為時間欄位，就用 formatTime 渲染
                    if (col.isTime) {
                        return {
                            ...base,
                            render: (h, params) => {
                                const raw = params.row[col.key];
                                return h("span", this.formatTime(raw));
                            },
                        };
                    }

                    // 如果外面有傳 render 進來，就直接用
                    if (typeof col.render === "function") {
                        base.render = col.render;
                    }

                    return base; // 預設渲染
                });
        },

        // 真正給 Table 用的欄位：固定 Checkbox 欄 + 使用者勾選欄位
        tableColumns() {
            const selectColumn = {
                title: " ",
                align: "center",
                slot: "select",
                width: 50,
            };

            return [selectColumn, ...this.displayedColumns];
        },
    },
    methods: {
        initInternalColumnOptions() {
            // 深拷貝，避免直接改 props
            this.internalColumnOptions = this.columnOptions.map((col) => ({
                ...col,
            }));
        },
        formatTime(time) {
            if (!time) return "";
            return moment(time)
                .utcOffset(8 * 60)
                .format("YYYY-MM-DD HH:mm:ss.SSS");
        },
        onChangePage(page) {
            this.$emit("change-page", page);
        },
        onChangePageSize(size) {
            this.$emit("change-page-size", size);
        },
    },
};
</script>



// WEBPACK FOOTER //
// src/components/Table/TableWithColumnDrawer.vue